import { createBrowserRouter, Navigate, useParams } from "react-router"
import { wrapCreateBrowserRouterV7 } from "@sentry/react"
import { sentryEnabled } from "@/lib/sentry"
import { RouteErrorBoundary } from "@/components/System"
import { AppLayout } from "@/components/Layout"
import {
  Landing,
  Login,
  Signup,
  SignupVerify,
  ForgotPassword,
  ResetPassword,
  AcceptInvitation,
  History,
  Dashboard,
  WorkspaceDetail,
  Settings,
  KnowledgeBasePage,
  BrandDetail,
  AgentPlayground,
  UserDetailPage,
  OAuthBackDev,
  TestOAuthBack,
  PaymentReturnBridge,
  NotificationEmailVerificationResult,
} from "@/pages"
import "./App.css"

export const AgentEvaluationRedirect = () => {
  const { workspaceId, agentId } = useParams()
  return (
    <Navigate
      to={`/workspaces/${workspaceId}/agents/${agentId}?tab=evaluation`}
      replace
    />
  )
}

const createConsoleRouter = sentryEnabled
  ? wrapCreateBrowserRouterV7(createBrowserRouter)
  : createBrowserRouter

export const Router: ReturnType<typeof createBrowserRouter> =
  createConsoleRouter([
    {
      path: "/login",
      element: <Login />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/signup",
      element: <Signup />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/signup/verify",
      element: <SignupVerify />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/accept-invitation",
      element: <AcceptInvitation />,
      errorElement: <RouteErrorBoundary />,
    },
    // TODO: Remove dev-only OAuth routes before merging to main.
    {
      path: "/oauth-back",
      element: <OAuthBackDev />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/test-oauth-back",
      element: <TestOAuthBack />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/payments/bridge",
      element: <PaymentReturnBridge />,
    },
    {
      path: "/notification-email-verification",
      element: <NotificationEmailVerificationResult />,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: "/",
      element: <AppLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: "/dashboard",
          element: <Dashboard />,
        },
        {
          path: "/workspaces/:workspaceId/agents/:agentId",
          element: <AgentPlayground />,
        },
        {
          path: "/workspaces/:workspaceId/agents/:agentId/evaluation",
          element: <AgentEvaluationRedirect />,
        },
        // Global histories removed; histories are under workspaces
        {
          path: "/workspaces",
          element: <Navigate to="/settings/organization/workspaces" replace />,
        },
        {
          path: "/workspaces/:workspaceId/histories/:historyId",
          element: <History />,
        },
        {
          path: "/workspaces/:workspaceId/users/:userId",
          element: <UserDetailPage />,
        },
        // Catch-all for other workspace routes should be last
        {
          path: "/workspaces/:workspaceId/*",
          element: <WorkspaceDetail />,
        },
        {
          path: "/settings/*",
          element: <Settings />,
        },
        {
          path: "/brands",
          element: <Navigate to="/settings/organization/brands" replace />,
        },
        {
          path: "/brands/:brandId",
          element: <BrandDetail />,
        },
        {
          path: "/knowledge-base",
          element: <KnowledgeBasePage />,
        },
      ],
    },
    {
      path: "/landing",
      element: <Landing />,
      errorElement: <RouteErrorBoundary />,
    },
  ])
